import React from 'react';
import { createUseStyles } from 'react-jss';
import { gql, useQuery } from '@apollo/client';
import { S, DARK } from '~/app/ui/tokens';
import { isAixcPagePath } from 'shared/lib/hasPermission';
import { summarizeUsage, plural } from './publishImpactSummary';

export const CUSTOM_WIDGET_USAGE = gql`
  query CustomWidgetUsage($name: String!) {
    customWidgetUsage(name: $name) {
      name
      publishedVersion
      pages {
        pageId
        path
        title
        publishedVersion
        pageVersionId
        instances
        followingLatest
        pinnedToDraft
        pinned
        pinnedVersions
        presets
      }
    }
  }
`;

type RedeployPage = { title: string; path: string; pageVersionId: string };

type Props = {
  widgetName: string;
  versionId: string;
  canDeploy: boolean;
  /** An AIXC deployer who isn't a super admin: the server only lets them deploy AIXC pages. */
  aixcOnly?: boolean;
  onRedeploy: (pages: RedeployPage[]) => void;
};

const MAX_LISTED = 5;

const box: React.CSSProperties = {
  textAlign: 'left',
  fontSize: '13px',
  lineHeight: 1.5,
  borderRadius: '6px',
  padding: '12px 14px',
  margin: '4px 0 16px'
};

// Colors live in classes so the dark theme can override them.
const useStyles = createUseStyles({
  box: {
    color: '#374151',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    [DARK]: { color: S.fg, background: S.chrome, borderColor: S.line }
  },
  redeploy: {
    background: '#111827',
    color: S.onFg,
    [DARK]: { background: S.fg }
  }
});

/**
 * Shows which live pages a newly published custom widget version reaches, and
 * offers to redeploy the ones that follow the latest published version.
 */
const PublishImpact = ({ widgetName, versionId, canDeploy, aixcOnly, onRedeploy }: Props) => {
  const c = useStyles();
  const { data, loading, error } = useQuery(CUSTOM_WIDGET_USAGE, {
    variables: { name: widgetName },
    fetchPolicy: 'network-only'
  });

  if (loading) return <div className={c.box} style={box}>Checking which live pages use this widget…</div>;
  if (error) return <div className={c.box} style={box}>Couldn’t check which pages use this widget: {error.message}</div>;

  const usage = summarizeUsage(data?.customWidgetUsage?.pages);
  if (!usage.total) return <div className={c.box} style={box}>No live pages use this widget yet.</div>;
  // A redeploy with any page the user may not deploy is refused as a whole, so offer only theirs.
  const redeployable = aixcOnly ? usage.redeployable.filter(p => isAixcPagePath(p.path)) : usage.redeployable;
  const notTheirs = usage.redeployable.length - redeployable.length;

  return (
    <div className={c.box} style={box} data-testid="publish-impact">
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Used on {plural(usage.total, 'live page', 'live pages')}</div>
      {usage.updating.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {plural(usage.updating.length, 'page follows', 'pages follow')} the latest published version
          and will show v{versionId} once {usage.updating.length === 1 ? 'it is' : 'they are'} deployed.
          {canDeploy && redeployable.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                className={c.redeploy}
                onClick={() => onRedeploy(redeployable.map(p => ({ title: p.title, path: p.path, pageVersionId: p.pageVersionId as string })))}
                style={{
                  border: 0, borderRadius: '4px', padding: '7px 12px', fontSize: '13px', cursor: 'pointer'
                }}
              >
                Redeploy {plural(redeployable.length, 'page', 'pages')} now
              </button>
            </div>
          )}
          {canDeploy && notTheirs > 0 && (
            <div style={{ marginTop: 6 }}>
              {plural(notTheirs, 'page outside AIXC needs', 'pages outside AIXC need')} a super admin to redeploy.
            </div>
          )}
        </div>
      )}
      {usage.pinnedOnly.length > 0 && (
        <div>
          {plural(usage.pinnedOnly.length, 'page stays', 'pages stay')} on a pinned version:
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {usage.pinnedOnly.slice(0, MAX_LISTED).map(p => (
              <li key={p.pageId}>{p.path} · v{p.pinnedVersions.join(', v')}</li>
            ))}
            {usage.pinnedOnly.length > MAX_LISTED && <li>and {usage.pinnedOnly.length - MAX_LISTED} more</li>}
          </ul>
          {usage.pinnedPresets.length > 0 && (
            <div style={{ marginTop: 6 }}>
              Pinned through {usage.pinnedPresets.length === 1 ? 'the preset' : 'the presets'} {usage.pinnedPresets.map(t => `“${t}”`).join(', ')}.
              Set {usage.pinnedPresets.length === 1 ? 'it' : 'them'} to “Latest published” in Widget Presets to move those pages.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PublishImpact;
